import React, {useCallback, useEffect, useState} from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaceIcon from "@mui/icons-material/Place";
import StreetviewIcon from "@mui/icons-material/Streetview";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import {useTranslation, withTranslation} from "react-i18next";
import {Virtuoso} from "react-virtuoso";
import {compose} from "redux";
import CustomEmpty from "../custom-empty";
import {DIMENSION_TYPE, getLabelField} from "./constants";
import {normalizeUrl} from "../../utils/urls";
import {formatIdentifyValue} from "./utils";

const linkIconStyle = {
  margin: "0 !important",
  color: theme => theme.palette.text.primary
};

const renderDetailsEntries = (details, columnLabels, columnTypes, timeFormatMap) => (
  <Box
    component="table"
    className="viewer-geojson-map__identify-results__item__details__table"
    sx={{
      width: "100%",
      borderCollapse: "collapse",
      tableLayout: "auto"
    }}
  >
    <Box component="tbody">
      {Object.keys(details ?? {}).map((key, idx) => {
        const rawValue = details?.[key];

        const isWebsiteType = columnTypes?.[key] === DIMENSION_TYPE.website;
        const normalizedUrl = isWebsiteType ? normalizeUrl(rawValue) : "";

        return (
          <Box component="tr" key={idx} className="viewer-geojson-map__identify-results__item__details__element">
            <Box
              component="td"
              className="viewer-geojson-map__identify-results__item__details__element__key"
              sx={{
                overflowWrap: "break-word",
                verticalAlign: "top",
                paddingBottom: "4px",
                paddingRight: "8px",
                fontWeight: "bold"
              }}
            >
              {columnLabels?.[key] || key}:
            </Box>
            <Box
              component="td"
              className="viewer-geojson-map__identify-results__item__details__element__value"
              sx={{
                overflowWrap: "break-word",
                verticalAlign: "top",
                paddingBottom: "4px"
              }}
            >
              {isWebsiteType && normalizedUrl.length > 0 ? (
                <Box
                  component="a"
                  href={normalizedUrl}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    color: "inherit",
                    textDecoration: "underline",
                    wordBreak: "break-all"
                  }}
                >
                  {rawValue}
                </Box>
              ) : (
                formatIdentifyValue(rawValue, columnTypes?.[key], timeFormatMap)
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

const IdentifyResultItem = ({
  feature,
  expanded,
  onChange,
  labelField,
  labelFieldList,
  columnLabels,
  columnTypes,
  timeFormatMap,
  featureDetails,
  onFeatureDetailsFetch,
  t
}) => (
  <Accordion
    id={`viewer-geojson-map__identify-results__item--${feature["id"]}`}
    className="viewer-geojson-map__identify-results__item"
    expanded={expanded}
    onChange={onChange}
  >
    <AccordionSummary
      className="viewer-geojson-map__identify-results__item__summary"
      sx={{
        padding: "4px 16px"
      }}
      expandIcon={<ExpandMoreIcon />}
    >
      <Typography
        sx={{
          fontWeight: expanded ? "bold" : "normal"
        }}
      >
        {getLabelField(feature, labelField, labelFieldList)}
      </Typography>
    </AccordionSummary>
    <Divider
      className="viewer-geojson-map__identify-results__item__details-divider"
      sx={{
        margin: "0 8px"
      }}
    />
    <AccordionDetails className="viewer-geojson-map__identify-results__item__details" sx={{fontSize: "14px"}}>
      {onFeatureDetailsFetch ? (
        featureDetails ? (
          renderDetailsEntries(featureDetails, columnLabels, columnTypes, timeFormatMap)
        ) : (
          <CustomEmpty text={t("modules.viewerGeojsonMap.identifyDialog.loading")} />
        )
      ) : (
        renderDetailsEntries(feature?.attributes, columnLabels, columnTypes, timeFormatMap)
      )}
    </AccordionDetails>
    {feature.latLongCoordinates && (
      <>
        <Divider
          className="viewer-geojson-map__identify-results__item__actions-divider"
          sx={{
            margin: "0 8px"
          }}
        />
        <AccordionActions className="viewer-geojson-map__identify-results__item__actions">
          <Tooltip title={t("modules.viewerGeojsonMap.identifyDialog.links.googleMaps")}>
            <IconButton
              href={`https://www.google.com/maps/search/?api=1&query=${feature.latLongCoordinates.latitude},${feature.latLongCoordinates.longitude}`}
              target="_blank"
              sx={linkIconStyle}
            >
              <PlaceIcon
                className="viewer-geojson-map__identify-results__item__actions__google-map-icon"
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("modules.viewerGeojsonMap.identifyDialog.links.streetView")}>
            <IconButton
              href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${feature.latLongCoordinates.latitude},${feature.latLongCoordinates.longitude}`}
              target="_blank"
              sx={linkIconStyle}
            >
              <StreetviewIcon
                className="viewer-geojson-map__identify-results__item__actions__street-view-icon"
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        </AccordionActions>
      </>
    )}
  </Accordion>
);

const IdentifyResults = ({
  identifyResults,
  onSelect,
  labelField,
  labelFieldList,
  columnLabels,
  columnTypes,
  timeFormatMap,
  featureDetails,
  onFeatureDetailsFetch,
  onFeatureDetailsClear
}) => {
  const {t} = useTranslation();

  const [expandedFeatureId, setExpandedFeatureId] = useState(false);

  const handleSelectFeature = useCallback(
    feature => (_, isExpanded) => {
      if ((identifyResults || []).length > 0) {
        setExpandedFeatureId(isExpanded ? feature.id : false);
        if (onSelect) {
          if (isExpanded) {
            onSelect([feature.id]);
            if (onFeatureDetailsClear) onFeatureDetailsClear();
            if (onFeatureDetailsFetch) onFeatureDetailsFetch(feature.attributes);
          } else {
            onSelect([]);
            // if (onFeatureDetailsClear) onFeatureDetailsClear();
          }
        }
      } else {
        if (onSelect) {
          onSelect([]);
        }
        setExpandedFeatureId(false);
      }
    },
    [identifyResults, onSelect, onFeatureDetailsFetch, onFeatureDetailsClear]
  );

  useEffect(() => {
    setExpandedFeatureId(false);
    let selectedFeatures = [];
    if ((identifyResults || []).length > 0) {
      const id = identifyResults[0]["id"];
      setExpandedFeatureId(id);
      selectedFeatures = [id];
    }
    if (onSelect) {
      onSelect(selectedFeatures);
      if (onFeatureDetailsClear) onFeatureDetailsClear();
      if (onFeatureDetailsFetch) onFeatureDetailsFetch(identifyResults[0].attributes);
    }
  }, [identifyResults, onSelect, onFeatureDetailsFetch, onFeatureDetailsClear]);

  return (
    <Box
      className="viewer-geojson-map__identify-results"
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "& .viewer-geojson-map__identify-results__item": {
          margin: "8px 0 !important"
        },
        "& .viewer-geojson-map__identify-results__item:first-of-type": {
          marginTop: "0 !important"
        }
      }}
    >
      <Virtuoso
        data={identifyResults}
        itemContent={(idx, feature) =>
          feature && (
            <IdentifyResultItem
              key={idx}
              feature={feature}
              expanded={expandedFeatureId === feature["id"]}
              onChange={handleSelectFeature(feature)}
              labelField={labelField}
              labelFieldList={labelFieldList}
              columnLabels={columnLabels}
              columnTypes={columnTypes}
              timeFormatMap={timeFormatMap}
              featureDetails={featureDetails}
              onFeatureDetailsFetch={onFeatureDetailsFetch}
              t={t}
            />
          )
        }
      />
    </Box>
  );
};

export default compose(withTranslation())(IdentifyResults);
